import React, { useState, useEffect } from "react";
import { getUserValidationStatus } from "../../services/antiFraudService";
import "./ValidationStatus.scss";

/**
 * ValidationStatus Component
 * Displays user's session validation status and history
 */
const ValidationStatus = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    flagged: 0,
    rejected: 0,
  });

  useEffect(() => {
    fetchValidationStatus();
  }, []);

  const fetchValidationStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserValidationStatus(10);
      
      if (data.sessions) {
        setSessions(data.sessions);
        
        // Calculate stats
        const newStats = {
          pending: 0,
          approved: 0,
          flagged: 0,
          rejected: 0,
        };
        
        data.sessions.forEach((session) => {
          const status = session.validationStatus;
          if (status === "pending") newStats.pending++;
          else if (status === "auto_approved" || status === "approved") newStats.approved++;
          else if (status === "flagged") newStats.flagged++;
          else if (status === "rejected") newStats.rejected++;
        });
        
        setStats(newStats);
      }
    } catch (err) {
      console.error("Error fetching validation status:", err);
      
      // Check if it's a 500 error (backend not implemented)
      if (err.response?.status === 500 || err.code === 'ERR_BAD_RESPONSE') {
        setError("⚠️ Backend anti-fraud endpoints are not yet implemented. The frontend UI is ready and waiting for backend deployment.");
        // Set empty data so UI can still render
        setSessions([]);
        setStats({
          pending: 0,
          approved: 0,
          flagged: 0,
          rejected: 0,
        });
      } else {
        setError("Failed to load validation status");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: "Pending", icon: "⏳", className: "status-pending" },
      auto_approved: { label: "Approved", icon: "✅", className: "status-approved" },
      approved: { label: "Approved", icon: "✅", className: "status-approved" },
      flagged: { label: "Under Review", icon: "⚠️", className: "status-flagged" },
      rejected: { label: "Rejected", icon: "❌", className: "status-rejected" },
    };
    
    const badge = badges[status] || { label: status, icon: "❓", className: "" };
    
    return (
      <span className={`status-badge ${badge.className}`}>
        <span className="status-icon">{badge.icon}</span>
        <span className="status-label">{badge.label}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="validation-status loading">
        <p>Loading validation status...</p>
      </div>
    );
  }

  if (error && sessions.length === 0) {
    return (
      <div className="validation-status error">
        <h2>Backend Not Ready</h2>
        <p>{error}</p>
        <button onClick={fetchValidationStatus}>Retry</button>
        <div style={{ marginTop: '20px', fontSize: '14px', color: '#666', textAlign: 'left' }}>
          <strong>Note:</strong> The frontend validation status component is fully implemented. 
          The backend needs to implement:
          <ul style={{ marginTop: '10px' }}>
            <li>GET /api/v1/recordings/validation-status</li>
            <li>POST /api/v1/recordings/:id/webcam-check</li>
            <li>POST /api/v1/recordings/:id/upload-evidence</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="validation-status">
      <div className="validation-header">
        <h2>Session Validation Status</h2>
        <button className="refresh-button" onClick={fetchValidationStatus}>
          🔄 Refresh
        </button>
      </div>

      <div className="validation-stats">
        <div className="stat-card stat-approved">
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card stat-flagged">
          <div className="stat-value">{stats.flagged}</div>
          <div className="stat-label">Under Review</div>
        </div>
        <div className="stat-card stat-rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      {stats.flagged > 0 && (
        <div className="validation-notice notice-warning">
          <span className="notice-icon">⚠️</span>
          <div className="notice-content">
            <strong>Sessions Under Review</strong>
            <p>
              You have {stats.flagged} session(s) awaiting admin review.
              Approved session earnings can still be withdrawn.
            </p>
          </div>
        </div>
      )}

      {stats.rejected > 0 && (
        <div className="validation-notice notice-error">
          <span className="notice-icon">❌</span>
          <div className="notice-content">
            <strong>Rejected Sessions</strong>
            <p>
              You have {stats.rejected} rejected session(s). 
              Please ensure your face is visible in the webcam and gameplay is genuine.
            </p>
          </div>
        </div>
      )}

      <div className="sessions-list">
        <h3>Recent Sessions</h3>
        
        {sessions.length === 0 ? (
          <p className="no-sessions">No sessions found.</p>
        ) : (
          <div className="sessions-table">
            {sessions.map((session) => (
              <div key={session.sessionId} className="session-row">
                <div className="session-info">
                  <div className="session-game">{session.gameName || "Unknown Game"}</div>
                  <div className="session-meta">
                    <span className="session-date">{formatDate(session.sessionStart)}</span>
                    {session.duration && (
                      <span className="session-duration">
                        • {formatDuration(session.duration)}
                      </span>
                    )}
                    {session.cashEarned !== undefined && (
                      <span className="session-earnings">
                        • ${session.cashEarned.toFixed(2)}
                      </span>
                    )}
                  </div>
                  {session.validationFlags && session.validationFlags.length > 0 && (
                    <div className="session-flags">
                      {session.validationFlags.map((flag, idx) => (
                        <div key={idx} className="flag-item">• {flag}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="session-status">
                  {getStatusBadge(session.validationStatus)}
                  {session.validationScore !== undefined && (
                    <div className="validation-score">
                      Score: {session.validationScore}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="validation-info">
        <h4>Understanding Validation Status</h4>
        <ul>
          <li>
            <strong>✅ Approved:</strong> Session validated and earnings added to your balance
          </li>
          <li>
            <strong>⏳ Pending:</strong> Awaiting video evidence upload
          </li>
          <li>
            <strong>⚠️ Under Review:</strong> Session flagged for manual admin review
          </li>
          <li>
            <strong>❌ Rejected:</strong> Session failed validation checks
          </li>
        </ul>
        
        <div className="validation-tips">
          <h5>Tips for Successful Validation:</h5>
          <ul>
            <li>Keep your face visible in the webcam throughout the session</li>
            <li>Sessions should be 10-240 minutes long</li>
            <li>Upload gameplay and webcam recordings within 24 hours</li>
            <li>Ensure genuine gameplay (no idle/automated activity)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ValidationStatus;


